import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthCardHeading } from '../components/AuthCardHeading.js';
import { AuthShell } from '../components/AuthShell.js';
import { WarningIcon } from '../components/icons/WarningIcon.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useAuth } from './AuthContext.js';

/** Fixed user-facing copy. The caught error is NEVER rendered: `handleCallback` throws strings
 *  like "State mismatch — possible CSRF attack" and "Nonce mismatch — possible replay attack",
 *  which are exception text, not copy for the focal line of a branded card. */
const CALLBACK_ERROR =
  "We couldn't complete your sign-in. Return to the sign-in page and try again.";

export function Callback() {
  const { handleCallback, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  // Mounted empty and filled on the next commit — a live region that already contains its text
  // at first paint is generally not announced.
  const [announcement, setAnnouncement] = useState('');
  const hasExchanged = useRef(false);

  useEffect(() => {
    setAnnouncement('Signing you in…');
  }, []);

  // Distinguishes the transient sign-in state from its failure in the tab and in history.
  usePageTitle(status === 'error' ? 'Sign-in error' : 'Signing in');

  useEffect(() => {
    // ORDER MATTERS: this early return must precede the `hasExchanged` write below. Setting the
    // ref first would mean a mount that bails here permanently blocks the exchange, and every
    // real login would hang on "Signing you in…".
    //
    // Gating on `isLoading` as well as `user` is what makes the guard real: AuthProvider's
    // silent refresh is asynchronous, so `user` is null on the first render even for a signed-in
    // user, while this effect fires on that same mount. Without the isLoading half, Back after a
    // successful login re-ran the exchange against already-consumed secrets and showed a CSRF
    // error to a user who was signed in.
    if (isLoading || user) return;
    if (hasExchanged.current) return;
    hasExchanged.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code || !state) {
      // The IdP signals its own failures here rather than by omitting params silently: a user who
      // cancels at the hosted UI arrives as ?error=access_denied. Log whatever it sent, or support
      // has nothing to diagnose from — a fixed string would discard the only useful detail.
      const idpError = searchParams.get('error');
      const idpErrorDescription = searchParams.get('error_description');
      if (idpError !== null) {
        console.error('OIDC callback returned an IdP error', {
          error: idpError,
          description: idpErrorDescription,
        });
      } else {
        console.error('OIDC callback reached without a code or state parameter');
      }
      setStatus('error');
      return;
    }

    handleCallback(code, state)
      .then(() => navigate('/timesheet', { replace: true }))
      .catch((err: unknown) => {
        console.error('OIDC callback exchange failed', err);
        setStatus('error');
      });
  }, [isLoading, user, searchParams, handleCallback, navigate]);

  if (!isLoading && user) {
    return <Navigate to="/timesheet" replace />;
  }

  return (
    <AuthShell>
      <AuthCardHeading />
      {/* No aria-busy on the live region below: on a live region it licenses AT to defer the
          announcement until it flips false, which never happens here, so it would suppress the
          very update the region exists to make. Mounted empty and filled in the effect above. */}
      {status === 'loading' && (
        <p className="signin-sub" aria-live="polite" aria-atomic="true">
          {announcement}
        </p>
      )}
      {status === 'error' && (
        <>
          <div className="auth-error" role="alert">
            <WarningIcon className="auth-error__icon" />
            <span>{CALLBACK_ERROR}</span>
          </div>
          {/* Pure navigation, so a link rather than the action button it replaces. The old Retry
              could never succeed — `handleCallback` consumes the PKCE verifier, state, and nonce
              from sessionStorage before the token POST, so every retry failed the state check
              first and reported a CSRF attack even for an ordinary network blip. Returning to "/"
              restarts the flow with a fresh verifier. */}
          <p className="signin-help">
            <Link to="/">Return to sign in</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
